import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { IChat, IMessage, IUser, TDocumentFormat, TImageFormat } from "@/lib/types";
import { AccountInfo } from "@azure/msal-browser";

export const MAX_CHAT_LIST = 10;

// Switch API_URL to be based on an environment variable instead of NODE_ENV

export const API_URL: string =
    process.env.NEXT_PUBLIC_ENV === "production"
        ? "https://fai-api.fender.com/" // production API URL
        : "https://active-fai-api.fendergarage.com/"; // development API URL

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

// Dev helper - mock user

// export const mockUser = (): IUser => {
//     return {
//         id: "1",
//         name: "Random User",
//         email: "mock@fender.com",
//     };
// };

// IndexedDB helpers

export const chatStorage = () => {
    let db: IDBDatabase | null = null;

    const init = (): Promise<IDBDatabase> =>
        new Promise((resolve, reject) => {
            const request = indexedDB.open("intelligence", 1);

            request.onerror = () => reject(request.error);

            request.onsuccess = () => {
                db = request.result;
                resolve(db);
            };
            request.onupgradeneeded = () => {
                db = request.result;
                db.createObjectStore("chats", { keyPath: "id" });
            };
        });

    const readChats = (): Promise<IChat[]> =>
        new Promise((resolve, reject) => {
            if (!db) return reject("Database not initialized");
            const transaction = db.transaction(["chats"], "readonly");
            const store = transaction.objectStore("chats");
            const request = store.getAll() as IDBRequest<IChat[]>;
            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve(request.result);
        });

    const writeChat = (chat: IChat): Promise<IDBValidKey> =>
        new Promise((resolve, reject) => {
            if (!db) return reject("Database not initialized");
            const transaction = db.transaction(["chats"], "readwrite");
            const store = transaction.objectStore("chats");
            const request = store.put(chat);
            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve(request.result);
        });

    const deleteChat = (id: string): Promise<void> =>
        new Promise((resolve, reject) => {
            if (!db) return reject("Database not initialized");
            const transaction = db.transaction(["chats"], "readwrite");
            const store = transaction.objectStore("chats");
            const request = store.delete(id);
            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve(request.result);
        });

    const close = () => {
        if (db) {
            db.close();
            db = null;
        }
    };

    return {
        init,
        readChats,
        writeChat,
        deleteChat,
        close,
    };
};

export const getUserFromInstance = (account: AccountInfo): IUser => {
    return {
        id: account.homeAccountId,
        name: account.name || "",
        email: account.username,
    };
};

// Chat helpers

export const isValidFile = (ext: string): boolean => {
    return ["pdf", "csv", "doc", "docx", "xls", "xlsx", "html", "txt", "md"].includes(ext);
};

export const isValidImage = (ext: string): boolean => {
    return ["png", "jpeg", "jpg", "gif", "webp"].includes(ext);
};

export const getExtensionFromFilename = (filename?: string): string | undefined => {
    const extension = filename?.split(".").pop()?.toLowerCase();
    switch (extension) {
        case "png":
            return "png";
        case "jpg":
            return "jpeg";
        case "jpeg":
            return "jpeg";
        case "gif":
            return "gif";
        case "webp":
            return "webp";
        case "pdf":
            return "pdf";
        case "csv":
            return "csv";
        case "doc":
            return "doc";
        case "docx":
            return "docx";
        case "xls":
            return "xls";
        case "xlsx":
            return "xlsx";
        case "html":
            return "html";
        case "md":
            return "md";
        // Add more extensions here if needed
        default:
            return "txt";
    }
};

export const createIMessage = (
    role: "user" | "assistant",
    text: string,
    base64file?: string,
    filename?: string
): IMessage => {
    const ext = getExtensionFromFilename(filename);
    const filenameWithoutExtension = filename?.split(".").slice(0, -1).join("") || "File";
    const message: IMessage = {
        content: [{ text }],
        role,
    };
    if (base64file && filename && ext) {
        if (isValidFile(ext)) {
            message.content.unshift({
                document: {
                    source: { bytes: base64file },
                    format: ext as TDocumentFormat,
                    name: filenameWithoutExtension,
                },
            });
        } else if (isValidImage(ext)) {
            message.content.unshift({
                image: { source: { bytes: base64file }, format: ext as TImageFormat },
            });
        }
    }
    return message;
};

// Stream utils

export const fetchStreamResponse = async (
    messages: string | undefined,
    jwt: string,
    abortSignal: AbortSignal
): Promise<ReadableStream<Uint8Array> | null> => {
    return await fetch(API_URL, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
        },
        credentials: 'omit',
        body: messages,
        signal: abortSignal,
    }).then((response) => response.body);
};

export async function* parseStreamResponse(stream: ReadableStream<Uint8Array>) {
    const reader = stream.getReader();
    const decoder = new TextDecoder();
    let done = false;
    while (!done) {
        const { value, done: _done } = await reader.read();
        if (value) {
            const text = decoder.decode(value, { stream: true });
            const jsonArray = text.split("}{");
            const jsonArrayClean = jsonArray.map((item, idx) => {
                if (jsonArray.length === 1) {
                    return item;
                } else if (idx === 0) {
                    return `${item}}`;
                } else if (idx === jsonArray.length - 1) {
                    return `{${item}`;
                } else {
                    return `{${item}}`;
                }
            });
            for (const json of jsonArrayClean) {
                yield JSON.parse(json);
            }
        }
        done = _done;
    }
}
